import { useEffect, useState } from "react";
import { toast } from "sonner";
import moment from "moment";
import { getDoctors, getAvailableSlots, editAppointment } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";

const VISIT_TYPES = [
  { key: "center", label: "Center Visit" },
  { key: "video", label: "Online" },
  { key: "home", label: "Home Visit" },
  { key: "walkin", label: "Walk In" },
];

// Must match backend constants/appointment.js EDIT_DISPOSITION exactly.
const DISPOSITIONS = [
  "Patient requested reschedule",
  "Doctor unavailable",
  "Data correction",
  "Duplicate entry",
  "Other",
];

export default function EditBookingModal({ open, onOpenChange, appointment, onSaved }) {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [isoDate, setIsoDate] = useState("");
  const [visitType, setVisitType] = useState("center");
  const [procedure, setProcedure] = useState("");
  const [slotInfo, setSlotInfo] = useState({ onLeave: false, slots: [] });
  const [slotTime, setSlotTime] = useState("");
  const [disposition, setDisposition] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !appointment) return;
    getDoctors({ limit: 100 }).then((res) => setDoctors(res.data));
    setDoctorId(appointment.doctor_id?._id);
    setIsoDate(appointment.iso_date);
    setVisitType(appointment.visit_type);
    setProcedure(appointment.procedure || "");
    setSlotTime(appointment.slot_time);
    setDisposition("");
  }, [open, appointment]);

  useEffect(() => {
    if (!open || !doctorId || !isoDate || !appointment) return;
    getAvailableSlots({ doctor_id: doctorId, iso_date: isoDate, exclude_appointment_id: appointment._id })
      .then((res) => setSlotInfo(res.data))
      .catch((err) => toast.error(err?.message || "Could not load available slots."));
  }, [open, doctorId, isoDate, appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!disposition) {
      toast.error("Please select a disposition for this edit.");
      return;
    }
    if (!slotTime) {
      toast.error("Please select a slot.");
      return;
    }
    setSaving(true);
    try {
      await editAppointment(appointment._id, {
        doctor_id: doctorId,
        iso_date: isoDate,
        slot_time: slotTime,
        visit_type: visitType,
        procedure,
        disposition,
      });
      toast.success("Booking updated.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Could not update booking.");
    } finally {
      setSaving(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit booking #{appointment._id.slice(-6)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Doctor</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={doctorId}
              onChange={(e) => setDoctorId(e.target.value)}
            >
              {doctors.map((d) => (
                <option key={d._id} value={d._id}>{d.name} — {d.specialty}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={isoDate} onChange={(e) => setIsoDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Visit type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={visitType}
                onChange={(e) => setVisitType(e.target.value)}
              >
                {VISIT_TYPES.map((v) => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Slot</Label>
            {slotInfo.onLeave ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                This doctor is on leave on the selected date.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slotInfo.slots.map((s) => (
                  <button
                    type="button"
                    key={s.time}
                    disabled={s.taken}
                    onClick={() => setSlotTime(s.time)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-sm",
                      s.taken && "cursor-not-allowed opacity-40 line-through",
                      slotTime === s.time
                        ? "border-primary bg-primary text-primary-foreground"
                        : s.vip
                          ? "border-warning/50 bg-warning/10 text-warning"
                          : "border-border"
                    )}
                  >
                    {s.vip && <span className="font-semibold">VIP Slot · </span>}
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Procedure</Label>
            <Input value={procedure} onChange={(e) => setProcedure(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Disposition (required) *</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            >
              <option value="">Select a reason for this edit…</option>
              {DISPOSITIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {appointment.last_edited_by_name && (
            <p className="text-xs text-muted-foreground">
              Last edited by {appointment.last_edited_by_name} on{" "}
              {moment(appointment.last_edited_at).format("D MMM YYYY, h:mm A")} — {appointment.last_disposition}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
